// Centralized deal stage constants and configurations
// Used across frontend components and for type safety

export const DEAL_STAGES = {
  NEW: 'new',
  CONTACT: 'contact',
  SCHEDULED: 'scheduled',
  PROPOSAL: 'proposal',
  NEGOTIATION: 'negotiation',
  WON: 'won',
  LOST: 'lost',
} as const;

export type DealStage = typeof DEAL_STAGES[keyof typeof DEAL_STAGES];

// Active pipeline stages (deals still being worked)
export const ACTIVE_STAGES: DealStage[] = ['new', 'contact', 'scheduled', 'proposal', 'negotiation'];

// Closed stages (final states)
export const CLOSED_STAGES: DealStage[] = ['won', 'lost'];

// Display order for stages
export const STAGE_ORDER: DealStage[] = ['new', 'contact', 'scheduled', 'proposal', 'negotiation', 'won', 'lost'];

// UI configuration for each stage (colors, etc.)
export const STAGE_CONFIG: Record<DealStage, { 
  color: string; 
  bgColor: string; 
  borderColor: string;
  countBg?: string;
  countText?: string;
}> = {
  new: { 
    color: 'text-slate-700', 
    bgColor: 'bg-slate-100', 
    borderColor: 'border-slate-300',
    countBg: 'bg-slate-100 dark:bg-slate-700',
    countText: 'text-slate-700 dark:text-slate-300'
  },
  contact: { 
    color: 'text-blue-700', 
    bgColor: 'bg-blue-100', 
    borderColor: 'border-blue-300',
    countBg: 'bg-blue-100 dark:bg-blue-900',
    countText: 'text-blue-700 dark:text-blue-300'
  },
  scheduled: { 
    color: 'text-purple-700', 
    bgColor: 'bg-purple-100', 
    borderColor: 'border-purple-300',
    countBg: 'bg-purple-100 dark:bg-purple-900',
    countText: 'text-purple-700 dark:text-purple-300'
  },
  proposal: { 
    color: 'text-amber-700', 
    bgColor: 'bg-amber-100', 
    borderColor: 'border-amber-300',
    countBg: 'bg-amber-100 dark:bg-amber-900',
    countText: 'text-amber-700 dark:text-amber-300'
  },
  negotiation: { 
    color: 'text-orange-700', 
    bgColor: 'bg-orange-100', 
    borderColor: 'border-orange-300',
    countBg: 'bg-orange-100 dark:bg-orange-900',
    countText: 'text-orange-700 dark:text-orange-300'
  },
  won: { 
    color: 'text-emerald-700', 
    bgColor: 'bg-emerald-100', 
    borderColor: 'border-emerald-300',
    countBg: 'bg-emerald-100 dark:bg-emerald-900',
    countText: 'text-emerald-700 dark:text-emerald-300'
  },
  lost: { 
    color: 'text-rose-700', 
    bgColor: 'bg-rose-100', 
    borderColor: 'border-rose-300',
    countBg: 'bg-rose-100 dark:bg-rose-900',
    countText: 'text-rose-700 dark:text-rose-300'
  },
};

// Legacy stage mapping for backwards compatibility with old data
export const LEGACY_STAGE_MAPPING: Record<string, DealStage> = {
  'NEW': 'new',
  'QUALIFYING': 'contact',
  'PROSPECTING': 'scheduled',
  'PROPOSAL': 'proposal',
  'NEGOTIATING': 'negotiation',
  'WON': 'won',
  'LOST': 'lost',
  'lead': 'new',
  'qualified': 'contact',
  'closed_won': 'won',
  'closed_lost': 'lost',
};
